import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Repeat, Loader2, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { tradeInsAPI } from '../../lib/api';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
// Uploads come back relative; a seeded or migrated photo may be absolute.
const photoSrc = (u) => (String(u).startsWith('http') ? u : `${API}${u}`);
const money = (n) => (n === null || n === undefined ? null : `$${Number(n).toLocaleString()}`);

// The desk's side of a trade-in: what the buyer sent, and the number that goes
// back. Resolving a trade-in is blocked server-side until a value exists — the
// queue must not clear with the buyer still waiting.
export const CCTradeInPanel = ({ req, onSaved }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(req.appraised_value ?? '');
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const n = Number(String(value).replace(/[^\d.]/g, ''));
        if (!Number.isFinite(n) || n < 0) {
            toast.error(t('tradeInDesk.badValue', 'Enter the appraised value')); return;
        }
        setBusy(true);
        try {
            await tradeInsAPI.setAppraisal(req.id, n);
            toast.success(t('tradeInDesk.saved', 'Appraised value recorded'));
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('tradeInDesk.failed', 'Could not save that value'));
        } finally { setBusy(false); }
    };

    const facts = [
        [t('tradeInDesk.trade', 'Trade-in'), req.trade_label],
        [t('tradeInDesk.mileage', 'Mileage'), req.mileage ? `${Number(req.mileage).toLocaleString()} km` : null],
        [t('tradeInDesk.condition', 'Condition'), req.condition],
        [t('tradeInDesk.vin', 'VIN'), req.vin],
        [t('tradeInDesk.payoff', 'Payoff'), money(req.payoff_amount)],
        [t('tradeInDesk.against', 'Against stock'), req.stock_number],
    ].filter(([, v]) => v);

    return (
        <div className="pt-3 border-t border-slate-800 space-y-3" data-testid="cc-trade-in-panel">
            <p className="text-xs font-semibold text-sky-600 dark:text-sky-300 flex items-center gap-1.5">
                <Repeat className="w-4 h-4" />{t('tradeInDesk.title', 'Trade-in appraisal')}
            </p>
            <div className="grid grid-cols-3 gap-2 text-xs">
                {facts.map(([k, v]) => (
                    <div key={k} className="contents">
                        <span className="text-slate-500">{k}</span>
                        <span className="col-span-2 text-slate-800 dark:text-slate-200 capitalize" data-testid={`cc-trade-in-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</span>
                    </div>
                ))}
            </div>
            {req.notes && <p className="text-xs text-slate-400 italic" data-testid="cc-trade-in-notes">“{req.notes}”</p>}
            {(req.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="cc-trade-in-photos">
                    {req.photos.map((url, i) => (
                        <a key={url} href={photoSrc(url)} target="_blank" rel="noreferrer"
                            className="w-16 h-16 rounded-lg overflow-hidden border border-slate-700">
                            <img src={photoSrc(url)} alt="" className="w-full h-full object-cover" data-testid={`cc-trade-in-photo-${i}`} />
                        </a>
                    ))}
                </div>
            )}
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                        {t('tradeInDesk.value', 'Appraised value')}
                    </label>
                    <div className="relative">
                        <DollarSign className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)}
                            placeholder="0" className="pl-8 h-10 bg-slate-800 border-slate-700 text-slate-100"
                            data-testid="cc-trade-in-value" />
                    </div>
                </div>
                <Button size="sm" onClick={save} disabled={busy} data-testid="cc-trade-in-save"
                    className="h-10 bg-sky-600 hover:bg-sky-500 text-white">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('tradeInDesk.save', 'Save value')}
                </Button>
            </div>
            <p className="text-[11px] text-slate-500" data-testid="cc-trade-in-hint">
                {t('tradeInDesk.hint', 'Record the number before you resolve — resolving without one is blocked.')}
            </p>
        </div>
    );
};

export default CCTradeInPanel;
